import { useContext, useRef, useEffect, useState } from "react";
import { API, Auth, Hub } from "aws-amplify";
import { logError, logInfo } from "util/logging";
import { ModalContext } from "contexts/Modal";
import { strNonEmpty } from "util/strings";

/* eslint-disable no-use-before-define */

const useEditProfile = (accountId) => {
  const { showModal, showConfirm } = useContext(ModalContext);

  // Track whether we have an authenticated session
  const [ready, setReady] = useState(false);

  // Prevent duplicate in-flight actions (StrictMode / rapid clicks)
  const inFlight = useRef(false);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) setReady(false);
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  const handleSaveProfile = async (
    event,
    accountId,
    initInputs,
    inputs,
    setModalOpen,
    setListUsers,
    profileId = "",
    updateAccount,
    dashboardPermission,
    brands,
    accounts,
    setDashboardPermission
  ) => {
    if (event) event.preventDefault();

    // Block while not authenticated
    if (!ready) {
      try {
        await Auth.currentAuthenticatedUser();
        setReady(true);
      } catch {
        showModal("Save User", "You are signed out. Please sign in and try again.");
        return;
      }
    }

    if (inFlight.current) return;
    inFlight.current = true;

    try {
      if (!validateInputs(profileId, inputs, showModal)) return;

      if (!strNonEmpty(profileId)) {
        await createUser(accountId, inputs);
        setModalOpen(false);
        showModal("Create User", "User successfully created");
        updateAccount(true);
      } else {
        const profileOk = await updateProfile(profileId, initInputs, inputs, accountId);
        const permissionOk = await updateUserPermission(profileId, dashboardPermission, brands, accounts, setDashboardPermission);
        if (!profileOk || !permissionOk) return;
        setModalOpen(false);
        showModal("Save User", "Save Successful");
        updateAccount(true);
      }
    } catch (err) {
      logError("useProfile.js: Error occurred while updating profile (2)", err);
      showModal("Save User", "Error occurred while saving user. (7)");
    } finally {
      inFlight.current = false;
    }
  };

  const handleDeactivateProfile = async (accountId, profileId, updateAccount) => {
    if (!ready) {
      try {
        await Auth.currentAuthenticatedUser();
        setReady(true);
      } catch {
        showModal("Deactivate User", "You are signed out. Please sign in and try again.");
        return;
      }
    }

    try {
      showConfirm(
        "Deactivate User",
        "Do you really want to deactivate the user?",
        async () => {
          try {
            await deactivateUser(accountId, profileId);
            updateAccount(true);
          } catch (err) {
            logError("useProfile.js deactivateUser Error", err);
            showModal("Deactivate User", "An error occurred while deactivating user.");
          }
        }
      );
    } catch (err) {
      logError("useProfile.js handleDeactivateProfile error: Unknown error", err);
    }
  };

  return { handleSaveProfile, handleDeactivateProfile };
};

// ---------- helpers ----------

// Validate that the inputs on the form are valid.
const validateInputs = (profileId, inputs, showModal) => {
  const nameNonEmpty = strNonEmpty(inputs.userName);
  const emailNonEmpty = strNonEmpty(inputs.userEmail);
  const roleNonEmpty = strNonEmpty(inputs.userRole);

  if (profileId) {
    if (!roleNonEmpty) {
      showModal("Save User", "Must select role.");
      return false;
    }
    return true;
  } else {
    if (!nameNonEmpty || !emailNonEmpty || !roleNonEmpty) {
      showModal(
        "Save User",
        `${nameNonEmpty ? "" : "Must include name. "}${
          emailNonEmpty ? "" : "Must include email address. "
        }${roleNonEmpty ? "" : "Must select role. "}`
      );
      return false;
    }
    return true;
  }
};

// Create a user in an account
const createUser = async (accountId, inputs) => {
  await Auth.currentAuthenticatedUser(); // ensure session
  const userToCreate = {
    name: inputs.userName,
    email: inputs.userEmail,
    role: inputs.userRole,
  };

  await API.post("AccountGatewayApi", `/account/${accountId}/profile`, {
    body: userToCreate
  });
};

// Patch a user’s role/profile fields
const updateProfile = async (profileId, initInputs, inputs, accountId) => {
  // nothing changed?
  if (
    initInputs.userName === inputs.userName &&
    initInputs.userEmail === inputs.userEmail &&
    initInputs.userPhone === inputs.userPhone &&
    initInputs.userRole === inputs.userRole
  ) {
    return true;
  }

  await Auth.currentAuthenticatedUser(); // ensure session

  // Clean empty values so we only send real changes if needed
  const body = { role: inputs.userRole };

  try {
    await API.patch(
      "AccountGatewayApi",
      `/account/${accountId}/profile/${profileId}`,
      {
        body
      }
    );
    return true;
  } catch (err) {
    logError("useProfile.js: Error occurred while updating profile (3)", err);
    return false;
  }
};

// Remove a user from an account
const deactivateUser = async (accountId, profileId) => {
  await Auth.currentAuthenticatedUser(); // ensure session
  const removedProfile = await API.del(
    "AccountGatewayApi",
    `/account/${accountId}/profile/${profileId}`
  );
  logInfo("useProfile adminDisableUser successful", removedProfile);
};

const updateUserPermission = async (userId, dashboardPermission, brands, accounts, setDashboardPermission) => {
  let bodyPermission;
  if (dashboardPermission === 'author') {
    bodyPermission = {
      "userId": userId,
      "userPermissions": 'author',
      "brandNames": brands,
      "accountIds": accounts
    }
  } else {
    bodyPermission = {
      "userId": userId,
      "userPermissions": 'reader'
    }
  }
  try {
    await API.put('DashboardApi', '/user', {
      body: bodyPermission,
    });

    // If everything updated successfully, then go back to the dashboard.
    setDashboardPermission(''); // reset dashboard permission to show spinner on modal until updated brand/account permissions can be retrieved
    return true;
  } catch (error) {
    logError("useProfile.js: Error occurred while updating permissions (3)", error);
    return false;
  }
}

export default useEditProfile;
